import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { 
  AcademicCapIcon, 
  HeartIcon, 
  UserGroupIcon, 
  ArrowRightIcon,
  SparklesIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  MapPinIcon,
  PlayIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'
import Counter from '../components/Counter'
import DonateModal from '../components/DonateModal'
import Timeline from '../components/Timeline'
import MovingTicker from '../components/MovingTicker'
import GoogleMap from '../components/GoogleMap'
import MovingText from '../components/MovingText'
import ErrorBoundary from '../components/ErrorBoundary'

// Image imports
import heroBg from '../assets/images/hero-bg.jpg.png'
import schoolFeedingImg from '../assets/images/SCHOOL FEEDING/School-children-eating-in-classroom-and-smiling-PACIEH.png'
import gmcHospitalImg from '../assets/images/GOODWILL MEDICAL CENTRES/Goodwill-Medical-centre-front-view.jpeg'
import teamWorkImg from '../assets/images/MOVING IMPACT IMAGES/_Z4A9857.jpg'
import b1Image from '../assets/images/MOVING IMPACT IMAGES/B (1).jpg'
import b2Image from '../assets/images/MOVING IMPACT IMAGES/B (2).jpg'
import b7Image from '../assets/images/MOVING IMPACT IMAGES/B (7).jpg'
import b11Image from '../assets/images/MOVING IMPACT IMAGES/B (11).jpg'
import b10Image from '../assets/images/MOVING IMPACT IMAGES/B (10).jpg'
import b12Image from '../assets/images/MOVING IMPACT IMAGES/B (12).jpg'
import b13Image from '../assets/images/MOVING IMPACT IMAGES/B (13).jpg'
import d1Image from '../assets/images/MOVING IMPACT IMAGES/d1.jpg'
import b9Image from '../assets/images/MOVING IMPACT IMAGES/B (9).jpg'
import b14Image from '../assets/images/MOVING IMPACT IMAGES/B (14).jpg'
import b15Image from '../assets/images/MOVING IMPACT IMAGES/B (15).jpg'
import b16Image from '../assets/images/MOVING IMPACT IMAGES/B (16).jpg'
import c2Image from '../assets/images/MOVING IMPACT IMAGES/C (2).png'

const PARTNERS = [
  { name: 'NTD NGO Network (NNN)', label: 'Global NTD Coalition Partner' },
  { name: 'WHO TDR Global', label: 'Research & Tropical Diseases' },
  { name: 'TY Danjuma Foundation', label: 'Healthcare Infrastructure Partner' },
  { name: 'DAHW Germany', label: 'Leprosy & TB Relief Association' },
  { name: 'University of Kigali', label: 'Academic & Health Research' },
  { name: 'Enugu State PHCDA', label: 'Maternal Health & TBAs' },
  { name: 'Anambra State Government', label: 'Education & Learning Assessments' },
  { name: 'SIHI Sweden', label: 'Social Innovation in Health' },
]

export default function Home() {
  const [donateModalOpen, setDonateModalOpen] = useState(false)

  const impactImages = [
    b1Image, b2Image, b7Image, b11Image, b10Image, b12Image,
    b13Image, d1Image, b9Image, b14Image, b15Image, b16Image, c2Image
  ].filter(Boolean)

  return (
    <ErrorBoundary>
      <Helmet>
        <title>PACIEH | Designing Impact With Purpose</title>
        <meta
          name="description"
          content="Pan-African Community Initiative on Education and Health. Transforming African communities through community-cooked meals, rural hospital care, and maternal health."
        />
      </Helmet>

      <MovingText />

      <div className="bg-[#F4F7F4] pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 sm:space-y-16">
          
          {/* ========================================================
              HERO SECTION (Inspired by Brightaid & Lightreach)
              Forest green rounded canvas with bold Swiss typography
             ======================================================== */}
          <section className="bg-forest rounded-[2.5rem] text-white p-6 sm:p-12 lg:p-16 shadow-2xl relative overflow-hidden">
            {/* Subtle radial glow */}
            <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#165a39] rounded-full blur-3xl opacity-40 pointer-events-none" />
            <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#13492f] rounded-full blur-3xl opacity-30 pointer-events-none" />

            <div className="relative z-10">
              {/* Top Capsule Badge & Secondary Subtext Header */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/15 px-4 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider text-[#9be868] w-fit">
                  <span className="w-2 h-2 rounded-full bg-[#8de058] animate-pulse" />
                  Building Brighter Paths • Est. 2013
                </div>

                <div className="text-xs text-white/70 max-w-sm hidden lg:block text-right">
                  Empowering rural communities through sustainable school feeding, maternal health & Goodwill Medical Centre.
                </div>
              </div>

              {/* Bold Headline & Action Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
                <div className="lg:col-span-8">
                  <h1 className="text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-black font-display tracking-tight leading-[0.98] text-white">
                    Impact Through <br />
                    <span className="text-[#8de058]">Kindness</span> & Purpose.
                  </h1>
                </div>

                <div className="lg:col-span-4 lg:pl-6 space-y-6">
                  <p className="text-base sm:text-lg text-white/85 leading-relaxed font-normal">
                    Every act of kindness brings nourishment, dignity, and life-saving healthcare to children and families across Africa.
                  </p>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setDonateModalOpen(true)}
                      className="btn-pill-lime group"
                    >
                      Donate Now
                      <span className="w-6 h-6 rounded-full bg-[#072617] text-white flex items-center justify-center text-xs group-hover:translate-x-0.5 transition-transform">
                        →
                      </span>
                    </button>

                    <Link
                      to="/programs"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm transition-all text-sm"
                    >
                      Our Programs
                    </Link>
                  </div>
                </div>
              </div>

              {/* Featured Showcase Photo Card with Overlay Metric */}
              <div className="relative rounded-3xl overflow-hidden aspect-[16/9] max-h-[520px] shadow-2xl border-2 border-white/10 bg-black/40 group">
                <img
                  src={schoolFeedingImg}
                  alt="School children smiling during PACIEH nutrition program"
                  className="w-full h-full object-cover object-center group-hover:scale-102 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                {/* Translucent Donors & Impact Floating Card (Inspired by Brightaid) */}
                <div className="absolute bottom-6 left-6 right-6 sm:right-auto sm:max-w-md bg-black/60 backdrop-blur-xl border border-white/20 rounded-2xl p-5 text-white shadow-2xl">
                  <div className="flex items-center justify-between text-xs text-white/70 mb-1">
                    <span className="font-semibold uppercase tracking-wider">Total Direct Beneficiaries</span>
                    <span className="font-bold text-[#8de058]">3,000+ Students</span>
                  </div>
                  <div className="text-2xl sm:text-3xl font-extrabold font-display text-white mb-2">
                    100% Community-Driven
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-white/20 h-2 rounded-full overflow-hidden mb-3">
                    <div className="bg-[#8de058] h-full rounded-full w-[88%]" />
                  </div>

                  <div className="flex items-center justify-between text-xs text-white/80">
                    <span>15+ Communities Reached</span>
                    <span className="font-semibold">Hot Meals & Health Care</span>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* ========================================================
              PARTNER LOGOS / CHANGEMAKERS STRIP
             ======================================================== */}
          <div className="text-center py-4">
            <p className="text-xs font-extrabold uppercase tracking-wider text-gray-500 mb-6">
              Supported by Changemakers & Institutional Partners Worldwide
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 items-center">
              {PARTNERS.map((partner) => (
                <div
                  key={partner.name}
                  className="bg-white rounded-2xl p-4 border border-gray-200/80 shadow-xs flex flex-col items-center justify-center text-center hover:border-forest/30 transition-colors"
                >
                  <span className="text-xs font-bold text-gray-900 leading-tight">
                    {partner.name}
                  </span>
                  <span className="text-[10px] text-gray-400 mt-0.5 line-clamp-1">
                    {partner.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ========================================================
              SECTION 2: MISSION NARRATIVE & THREE-PHOTO GALLERY
              (Inspired by Lightreach / Brightaid)
             ======================================================== */}
          <section className="bg-white rounded-[2.5rem] p-8 sm:p-14 border border-gray-200 shadow-sm">
            {/* Top Triple Photo Showcase with Fine Borders */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
              <div className="relative rounded-3xl overflow-hidden aspect-[4/3] border-4 border-[#EDF4ED] shadow-md group">
                <img
                  src={schoolFeedingImg}
                  alt="School children feeding"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <span className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-md text-forest text-xs font-bold px-3 py-1 rounded-full">
                  School Feeding
                </span>
              </div>

              <div className="relative rounded-3xl overflow-hidden aspect-[4/3] border-4 border-[#EDF4ED] shadow-md group">
                <img
                  src={gmcHospitalImg}
                  alt="Goodwill Medical Centre"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <span className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-md text-forest text-xs font-bold px-3 py-1 rounded-full">
                  Goodwill Medical Centre
                </span>
              </div>

              <div className="relative rounded-3xl overflow-hidden aspect-[4/3] border-4 border-[#EDF4ED] shadow-md group">
                <img
                  src={teamWorkImg}
                  alt="Health outreach team"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <span className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-md text-forest text-xs font-bold px-3 py-1 rounded-full">
                  Community Health Workers
                </span>
              </div>
            </div>

            {/* Two-Column Bold Typographic Story */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-5 space-y-4">
                <div className="tag-capsule bg-[#EDF4ED] text-forest">
                  Who We Are
                </div>
                <h2 className="text-3xl sm:text-5xl font-black font-display tracking-tight text-gray-900 leading-[1.05]">
                  Driven by Compassion, <br />
                  <span className="text-forest">Guided by Humanity.</span>
                </h2>

                <div className="flex flex-wrap gap-2 pt-2">
                  {['Transparent', 'Community Led', 'School Meals', 'Eye Surgery', 'Maternal Health'].map((badge) => (
                    <span
                      key={badge}
                      className="px-3.5 py-1.5 rounded-full bg-slate-100 text-gray-700 text-xs font-bold border border-gray-200"
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-7 space-y-6 lg:pl-6 text-gray-700">
                <p className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug font-display">
                  With community at our core, we deliver essential aid to children and families facing hardship across Africa.
                </p>
                <p className="text-base text-gray-600 leading-relaxed font-normal">
                  Founded by WHO Laureate Professor Uche Amazigo, PACIEH believes that sustainable giving is not a temporary handout — it is a shared covenant. From school mothers cooking hot meals to ophthalmologists restoring sight at Goodwill Medical Centre, our programs create permanent ladders out of poverty.
                </p>

                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <Link
                    to="/about"
                    className="btn-pill-forest group"
                  >
                    Learn More
                    <span className="w-5 h-5 rounded-full bg-white/20 text-white flex items-center justify-center text-xs group-hover:translate-x-0.5 transition-transform">
                      →
                    </span>
                  </Link>

                  <Link
                    to="/team"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold bg-white text-gray-800 border border-gray-300 hover:bg-gray-50 text-sm"
                  >
                    Our Team →
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* ========================================================
              SECTION 3: DEEP FOREST "OUR STORY" + STAT CARDS
              (Inspired by Lightreach card layout)
             ======================================================== */}
          <section className="bg-forest rounded-[2.5rem] text-white p-8 sm:p-14 shadow-2xl relative overflow-hidden">
            <div className="relative z-10">
              {/* Header */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-12">
                <div className="lg:col-span-6 space-y-3">
                  <div className="tag-capsule bg-[#8de058] text-[#072617]">
                    Our Story
                  </div>
                  <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black font-display tracking-tight text-white leading-[1.05]">
                    It Started With a Meal and a Promise.
                  </h2>
                </div>

                <div className="lg:col-span-6 space-y-6 lg:pl-6">
                  <p className="text-base sm:text-lg text-white/85 leading-relaxed font-normal">
                    PACIEH began when mothers in rural primary schools stepped forward to take turns cooking nutritious meals so their children could stay in class and learn. That grassroots spark grew into a comprehensive healthcare and education movement.
                  </p>

                  <Link
                    to="/programs"
                    className="btn-pill-lime group"
                  >
                    Read The Full Story
                    <span className="w-5 h-5 rounded-full bg-[#072617] text-white flex items-center justify-center text-xs group-hover:translate-x-0.5 transition-transform">
                      →
                    </span>
                  </Link>
                </div>
              </div>

              {/* Bottom split: Team Photo + Metric Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-7 rounded-3xl overflow-hidden aspect-[16/10] border-2 border-white/10 shadow-xl bg-black/40">
                  <img
                    src={teamWorkImg}
                    alt="PACIEH Field Team and Community Partners"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="lg:col-span-5 grid grid-cols-2 gap-4">
                  <div className="bg-white text-gray-900 rounded-3xl p-6 border-2 border-[#8de058] shadow-lg flex flex-col justify-between">
                    <div className="text-3xl sm:text-4xl font-black font-display text-forest">
                      <Counter end={3000} suffix="+" />
                    </div>
                    <div className="text-xs font-extrabold text-gray-800 uppercase tracking-wider mt-2">
                      Students Supported
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1">Daily school feeding & learning tests</p>
                  </div>

                  <div className="bg-white text-gray-900 rounded-3xl p-6 border-2 border-[#8de058] shadow-lg flex flex-col justify-between">
                    <div className="text-3xl sm:text-4xl font-black font-display text-forest">
                      <Counter end={15} suffix="+" />
                    </div>
                    <div className="text-xs font-extrabold text-gray-800 uppercase tracking-wider mt-2">
                      Communities Reached
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1">Across Anambra and Enugu states</p>
                  </div>

                  <div className="bg-white text-gray-900 rounded-3xl p-6 border-2 border-[#8de058] shadow-lg flex flex-col justify-between">
                    <div className="text-3xl sm:text-4xl font-black font-display text-forest">
                      <Counter end={5000} suffix="+" />
                    </div>
                    <div className="text-xs font-extrabold text-gray-800 uppercase tracking-wider mt-2">
                      Medical Consultations
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1">Goodwill Medical Centre & eye care</p>
                  </div>

                  <div className="bg-white text-gray-900 rounded-3xl p-6 border-2 border-[#8de058] shadow-lg flex flex-col justify-between">
                    <div className="text-3xl sm:text-4xl font-black font-display text-forest">
                      <Counter end={500} suffix="+" />
                    </div>
                    <div className="text-xs font-extrabold text-gray-800 uppercase tracking-wider mt-2">
                      Families via Avah Bridge
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1">Direct access to emergency care</p>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* ========================================================
              SECTION 4: SWISS POSTER TYPOGRAPHIC MANIFESTO
              (Inspired by Images 3, 4, 5 by Akinbinu Akintayo)
             ======================================================== */}
          <section className="bg-white rounded-[2.5rem] p-8 sm:p-16 border border-gray-200 shadow-sm text-left">
            <div className="max-w-5xl">
              <div className="tag-capsule bg-slate-100 text-gray-800 mb-6">
                Our Conviction
              </div>

              {/* Bold high-contrast typographic statement */}
              <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black font-display tracking-tight text-gray-900 leading-[1.08] mb-8">
                Community is the driver, not a bystander. <br />
                <span className="text-gray-400 font-normal">
                  When mothers cook, children thrive. When rivers are bridged, lives are saved.
                </span>{' '}
                Sustainable development happens from the roots up.
              </h2>

              <div className="pt-8 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs font-mono text-gray-500 uppercase tracking-wider">
                <div>PACIEH Field Philosophy • Est. 2013</div>
                <div>Pan-African Initiative</div>
                <div>Enugu & Anambra State, Nigeria</div>
              </div>
            </div>
          </section>

          {/* ========================================================
              FEATURED INTERNATIONAL REPORT: KIGALI NNN CONFERENCE
             ======================================================== */}
          <section className="bg-gradient-to-br from-[#0B3B24] to-[#124b2f] rounded-[2.5rem] text-white p-8 sm:p-14 shadow-2xl relative overflow-hidden">
            <div className="absolute -top-32 -right-32 w-80 h-80 bg-[#165a39] rounded-full blur-3xl opacity-40 pointer-events-none" />
            <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-[#13492f] rounded-full blur-3xl opacity-30 pointer-events-none" />

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7 space-y-5">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-3.5 py-1.5 rounded-full text-xs font-black uppercase tracking-wider text-[#8de058]">
                    <span className="w-2 h-2 rounded-full bg-[#8de058] animate-pulse" />
                    Latest Global Mission Report
                  </span>
                  <span className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full text-xs text-white/90">
                    <MapPinIcon className="h-3.5 w-3.5 text-[#8de058]" />
                    Kigali Marriott Hotel, Rwanda
                  </span>
                </div>

                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black font-display tracking-tight text-white leading-tight">
                  17th Annual NTD NGO Network Conference
                  <span className="block text-[#8de058]">Official Mission & Partnership Report</span>
                </h2>

                <p className="text-base text-white/85 leading-relaxed">
                  PACIEH CEO <strong className="text-white font-bold">Prof. Chinyelu Angela Ekwunife</strong> led official delegations to the 17th Annual NNN Conference in Kigali, establishing bilateral partnerships with <strong>MITOSATH</strong>, <strong>DAHW Germany</strong>, and the <strong>University of Kigali</strong> to accelerate community-driven NTD elimination and integrated school health across Africa.
                </p>

                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <a
                    href="/reports/Kigali-Report-2026.docx"
                    download="PACIEH-Kigali-Report-2026.docx"
                    className="btn-pill-lime text-xs sm:text-sm shadow-xl inline-flex items-center gap-2"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Download Kigali Report (DOCX • 24.2 MB)
                  </a>

                  <Link
                    to="/reports"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold bg-white/10 hover:bg-white/20 text-white border border-white/25 backdrop-blur-sm transition-all text-xs sm:text-sm"
                  >
                    <DocumentTextIcon className="h-4 w-4 text-[#8de058]" />
                    Read Overview in Reports
                  </Link>

                  <Link
                    to="/gallery"
                    className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full text-xs font-semibold text-white/80 hover:text-white transition-colors"
                  >
                    View Photos →
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="relative rounded-3xl overflow-hidden aspect-[4/3] border-2 border-white/20 shadow-2xl group bg-black/40">
                  <img
                    src="/images/KIGALI CONFERENCE/kigali-opening-ceremony.jpeg"
                    alt="Kigali NNN Conference opening ceremony"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-black/60 backdrop-blur-md px-2.5 py-1 rounded text-[#8de058] border border-white/10">
                      29 Sept. – 1 Oct. 2026 • Kigali, Rwanda
                    </span>
                    <p className="text-xs font-medium text-white/95 mt-1.5 line-clamp-2">
                      CEO Prof. Chinyelu Angela Ekwunife with international delegates at the Kigali Marriott Convention Centre
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ========================================================
              SECTION 5: SIGNATURE BREAKTHROUGH: AVAH BRIDGE & GMC
             ======================================================== */}
          <section className="bg-white rounded-[2.5rem] p-8 sm:p-14 border border-gray-200 shadow-sm">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <div className="tag-capsule bg-[#EDF4ED] text-forest mb-3">
                Signature Project
              </div>
              <h2 className="text-3xl sm:text-5xl font-black font-display tracking-tight text-gray-900">
                The Avah Bridge & Goodwill Medical Centre
              </h2>
              <p className="text-base text-gray-600 mt-3">
                How building a physical bridge over the Avah River unlocked emergency healthcare for over 500 low-income families.
              </p>
            </div>

            {/* Video Player */}
            <div className="relative pt-[56.25%] w-full rounded-3xl overflow-hidden shadow-2xl mb-10 border border-gray-200 bg-black">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/_KjmZK8FBUI?si=N16NzZirNt8_6wkW&autoplay=1&mute=1"
                title="PACIEH Impact Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>

            {/* Quote & Details */}
            <div className="bg-[#F4F7F4] rounded-3xl p-8 border border-gray-200 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7 space-y-4">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-forest text-white">
                  Commissioned April 2025
                </span>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-forest font-display">
                  Safe Passage in Dignity and Peace
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Before the bridge, expectant mothers and children in Ifo, Ugbo-Ezeji, and Ugbo-Paul had to wade across the Avah river on foot. Today, an entire region reaches Goodwill Medical Centre in safety.
                </p>
                <Link
                  to="/goodwill-medical"
                  className="btn-pill-forest text-xs"
                >
                  Explore Goodwill Medical Centre →
                </Link>
              </div>

              <div className="md:col-span-5 bg-white p-6 rounded-2xl border-l-4 border-[#8de058] shadow-sm">
                <blockquote className="text-gray-900 text-base italic leading-relaxed mb-3">
                  "Before the bridge, we lost lives during emergencies. Now we arrive at the hospital dry and alive."
                </blockquote>
                <div className="text-xs font-bold text-forest uppercase tracking-wider">
                  — Community Elder, Ugbo-Ezeji
                </div>
              </div>
            </div>
          </section>

          {/* ========================================================
              PHOTO TICKER & FIELD MEMORIES
             ======================================================== */}
          <section className="bg-white rounded-[2.5rem] p-8 sm:p-12 border border-gray-200 shadow-sm">
            <div className="text-center mb-8">
              <div className="tag-capsule bg-[#EDF4ED] text-forest mb-2">
                Field Memories
              </div>
              <h2 className="text-3xl sm:text-4xl font-black font-display text-gray-900 tracking-tight">
                Our Impact in Pictures
              </h2>
            </div>

            <MovingTicker images={impactImages} />

            <div className="flex justify-center mt-8">
              <Link
                to="/gallery"
                className="btn-pill-forest"
              >
                View Full Photo Gallery →
              </Link>
            </div>
          </section>

          {/* Historical Timeline */}
          <Timeline />

          {/* Live Google Map */}
          <GoogleMap />

          {/* ========================================================
              FINAL CALL TO ACTION BANNER (Deep Forest with Lime)
             ======================================================== */}
          <section className="bg-forest rounded-[2.5rem] text-white p-8 sm:p-16 shadow-2xl relative overflow-hidden text-center">
            <div className="relative z-10 max-w-3xl mx-auto space-y-6">
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-[#9be868]">
                <HeartIcon className="h-4 w-4" />
                Partner With Us Today
              </div>

              <h2 className="text-4xl sm:text-6xl font-black font-display tracking-tight text-white leading-tight">
                Be Part of Transforming <br />
                <span className="text-[#8de058]">African Communities.</span>
              </h2>

              <p className="text-base sm:text-lg text-white/85 leading-relaxed font-normal">
                Your donation directly funds school lunches cooked by mothers, cataract surgeries, maternal kits for birth attendants, and clean water.
              </p>

              <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setDonateModalOpen(true)}
                  className="btn-pill-lime text-base px-8 py-3.5 group"
                >
                  Donate to PACIEH
                  <span className="w-6 h-6 rounded-full bg-[#072617] text-white flex items-center justify-center text-xs group-hover:translate-x-0.5 transition-transform">
                    →
                  </span>
                </button>

                <a
                  href="https://forms.gle/bss8TuKU6U9xXCZ89"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-bold bg-white/10 hover:bg-white/20 text-white border border-white/25 backdrop-blur-sm text-base transition-all"
                >
                  Join as Volunteer
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* Global Donation Modal */}
      <DonateModal
        isOpen={donateModalOpen}
        onClose={() => setDonateModalOpen(false)}
      />
    </ErrorBoundary>
  )
}